import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle, Wrench } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryState, parseAsString, createParser } from 'nuqs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { JobCardDataTable } from './JobCardDataTable';
import {
  columns,
  editJobCardEvent,
  cancelJobCardEvent,
  convertToInvoiceEvent,
  advanceStatusEvent,
} from './columns';
import {
  listJobCards,
  updateJobCardStatus,
  getJobCard,
} from '@/app/services/jobCardService';
import { JobCardListItem, JobCardStatus } from '@/app/types/jobCard';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';

interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

type SortDirection = 'asc' | 'desc';

const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'transaction_date', desc: true }];
    const [field, direction] = value.split('-');
    return [
      {
        id: field || 'transaction_date',
        desc: direction === 'desc',
      },
    ];
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'transaction_date-desc';
    const sort = value[0];
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`;
  },
});

const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10);
    return isNaN(page) || page < 1 ? 0 : page - 1;
  },
  serialize: (value: number) => {
    return String(value + 1);
  },
});

type FilterValue = string | number | boolean | { from?: string; to?: string };

const filtersParser = createParser({
  parse: (value: string): Record<string, FilterValue> => {
    try {
      return value ? JSON.parse(decodeURIComponent(value)) : {};
    } catch {
      return {};
    }
  },
  serialize: (value: Record<string, FilterValue>) => {
    return Object.keys(value).length > 0 ? encodeURIComponent(JSON.stringify(value)) : '';
  },
});

const JobCardListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();

  const [page, setPage] = useQueryState('page', pageParser.withDefault(0));
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''));
  const [sorting, setSorting] = useQueryState(
    'sort',
    sortingParser.withDefault([{ id: 'transaction_date', desc: true }])
  );
  const [filters, setFilters] = useQueryState('filters', filtersParser.withDefault({}));
  const [limit] = useState<number>(10);

  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCardListItem | null>(null);

  const getSortParams = useCallback(() => {
    if (sorting.length === 0)
      return {
        sort_by: 'transaction_date',
        sort_dir: 'desc' as SortDirection,
      };

    const sort = sorting[0];
    const direction: SortDirection = sort.desc ? 'desc' : 'asc';

    return {
      sort_by: sort.id,
      sort_dir: direction,
    };
  }, [sorting]);

  const buildFilterParams = (
    filters: Record<string, string | number | boolean | { from?: string; to?: string }>
  ): string => {
    const filterArray: { field: string; value: unknown; op: string }[] = [];

    Object.entries(filters).forEach(([field, value]) => {
      if (value === undefined || value === null || value === '') return;

      // Handle date range filters
      if (field === 'transaction_date' && typeof value === 'object' && 'from' in value) {
        const dateFilter = value as { from?: string; to?: string };

        if (dateFilter.from) {
          filterArray.push({
            field: field,
            value: `${dateFilter.from} 00:00:00`,
            op: 'gte',
          });
        }

        if (dateFilter.to) {
          filterArray.push({
            field: field,
            value: `${dateFilter.to} 23:59:59`,
            op: 'lte',
          });
        }
        return;
      }

      // Handle text fields with contains search
      if (
        field === 'document_number' ||
        field === 'vehicle.registration_number' ||
        field === 'party.name'
      ) {
        filterArray.push({
          field: field,
          value: `%${value}%`,
          op: 'ilike',
        });
        return;
      }

      // Handle exact match fields (status, priority, job_card_type)
      filterArray.push({
        field: field,
        value: String(value),
        op: 'eq',
      });
    });

    return JSON.stringify(filterArray);
  };

  const handleFilterChange = useCallback(
    (
      newFilters: Record<string, string | number | boolean | { from?: string; to?: string }>
    ): void => {
      setFilters(newFilters);
      setPage(0);
    },
    [setFilters, setPage]
  );

  const {
    data: jobCardsData,
    isLoading,
    isError,
    error,
  } = useQuery<ExtendedPaginatedResponse<JobCardListItem>>({
    queryKey: ['job-cards', page, limit, search, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;

      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
      };

      if (search) {
        params.search_query = search;
      }

      if (Object.keys(filters).length > 0) {
        params.filters = buildFilterParams(filters);
      }

      return listJobCards(params);
    },
    enabled: !!currentOrganizationId,
  });

  const pageCount =
    jobCardsData?.page_count ??
    jobCardsData?.pageCount ??
    jobCardsData?.total_pages ??
    jobCardsData?.totalPages ??
    (jobCardsData ? Math.ceil(jobCardsData.total / limit) : 0);

  const cancelJobCardMutation = useMutation({
    mutationFn: (id: number) => updateJobCardStatus(id, { status: 'cancelled' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-cards'] });
      setIsCancelAlertOpen(false);
      setSelectedJobCard(null);
      toast.success('Job card cancelled successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel job card: ${error.message}`);
    },
  });

  const [isConvertLoading, setIsConvertLoading] = useState(false);

  // Event handlers for table actions
  React.useEffect(() => {
    const handleEditJobCard = (event: CustomEvent<{ jobCard: JobCardListItem }>): void => {
      navigate(`/workshop/job-cards/detail/${event.detail.jobCard.id}`);
    };

    const handleCancelJobCard = (event: CustomEvent<{ jobCard: JobCardListItem }>): void => {
      setSelectedJobCard(event.detail.jobCard);
      setIsCancelAlertOpen(true);
    };

    const handleConvertToInvoice = async (event: CustomEvent<{ jobCard: JobCardListItem }>): Promise<void> => {
      const listItem = event.detail.jobCard;
      setIsConvertLoading(true);
      try {
        // Fetch full job card with items for pre-population
        const fullJobCard = await getJobCard(listItem.id);
        const invoiceData = {
          party_id: fullJobCard.party_id,
          site_id: fullJobCard.site_id,
          reference_number: fullJobCard.reference_number || '',
          narration: `From Job Card #${fullJobCard.document_number}`,
          items: fullJobCard.items?.map((item) => ({
            ...item,
            item_id: item.item_id,
            description: item.notes || '',
            quantity: item.quantity,
            unit_id: item.unit_id || 1,
            rate: item.rate,
            discount_percent: item.discount_percent || 0,
            discount_amount: item.discount_amount || 0,
            tax_rate_id: item.tax_rate_id,
          })) || [],
          transaction_date: new Date(),
          due_date: null,
          voucher_type_id: 1,
          total_amount: fullJobCard.total_amount,
        };

        navigate('/sales-invoices/new', {
          state: {
            duplicateInvoice: invoiceData,
            fromDuplicate: true,
            fromJobCard: true,
            jobCardId: listItem.id,
            jobCardNumber: fullJobCard.document_number,
          }
        });
        toast.info('Job card data loaded. Review and save the invoice.');
      } catch (error) {
        toast.error(`Failed to load job card: ${(error as Error).message}`);
      } finally {
        setIsConvertLoading(false);
      }
    };

    const statusLabels: Record<string, string> = {
      arrival: 'Arrival',
      inspection: 'Inspection',
      quoted: 'Quoted',
      approved: 'Approved',
      under_service: 'Under Service',
      quality_check: 'Quality Check',
      ready: 'Ready',
      delivered: 'Delivered',
      invoiced: 'Invoiced',
      cancelled: 'Cancelled',
    };

    const handleAdvanceStatus = async (event: CustomEvent<{ id: number; nextStatus: string }>): Promise<void> => {
      const { id, nextStatus } = event.detail;
      try {
        await updateJobCardStatus(id, { status: nextStatus as JobCardStatus });
        queryClient.invalidateQueries({ queryKey: ['job-cards'] });
        toast.success(`Status updated to ${statusLabels[nextStatus] || nextStatus}`);
      } catch (error) {
        toast.error(`Failed to update status: ${(error as Error).message}`);
      }
    };

    document.addEventListener(editJobCardEvent, handleEditJobCard as EventListener);
    document.addEventListener(cancelJobCardEvent, handleCancelJobCard as EventListener);
    document.addEventListener(convertToInvoiceEvent, handleConvertToInvoice as EventListener);
    document.addEventListener(advanceStatusEvent, handleAdvanceStatus as EventListener);

    return () => {
      document.removeEventListener(editJobCardEvent, handleEditJobCard as EventListener);
      document.removeEventListener(cancelJobCardEvent, handleCancelJobCard as EventListener);
      document.removeEventListener(convertToInvoiceEvent, handleConvertToInvoice as EventListener);
      document.removeEventListener(advanceStatusEvent, handleAdvanceStatus as EventListener);
    };
  }, [navigate, queryClient]);

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearch(value);
      setPage(0);
    },
    [setSearch, setPage]
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState): void => {
      setSorting(newSorting);
      setPage(0);
    },
    [setSorting, setPage]
  );

  const handlePageChange = useCallback(
    (newPage: number): void => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleCancelJobCard = (): void => {
    if (selectedJobCard) {
      cancelJobCardMutation.mutate(selectedJobCard.id);
    }
  };


  return (
    <div className="mx-auto px-3 sm:px-6 lg:container py-3 sm:py-6 space-y-3 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 sm:h-6 sm:w-6" />
          <h1 className="text-lg sm:text-2xl font-bold">Job Cards</h1>
        </div>
        <Button asChild className="h-10 sm:h-9 shrink-0">
          <Link to="/workshop/job-cards/detail">
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">New Job Card</span>
            <span className="sm:hidden">New</span>
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Error loading job cards: {(error as Error).message}</AlertDescription>
        </Alert>
      )}

      <JobCardDataTable
        data={jobCardsData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSearchChange={handleSearchChange}
        searchValue={search}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
      />

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={isCancelAlertOpen} onOpenChange={setIsCancelAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Job Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel job card{' '}
              <span className="font-semibold">{selectedJobCard?.document_number}</span>? This will
              mark the job card as cancelled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelJobCard}
              disabled={cancelJobCardMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelJobCardMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Yes, cancel job card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default JobCardListPage;
